import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Search,
  Add,
  Home as HomeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setSchemas,
  setSelectedSchema,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedSchema,
  addSchema,
  updateSchema as updateSchemaAction,
  deleteSchema as deleteSchemaAction,
} from '../../store/slices/schemasSlice';
import {
  GET_SCHEMAS_LIST,
  GET_SCHEMA,
} from '../../graphql/queries/schemas.queries';
import {
  CREATE_SCHEMA,
  UPDATE_SCHEMA,
  DELETE_SCHEMA,
} from '../../graphql/mutations/schemas.mutations';
import {
  FindSchemaListInput,
  FindSchemaInput,
  CreateSchemaInput,
  UpdateSchemaInput,
  DeleteSchemaInput,
  CreateSchemaResponse,
  UpdateSchemaResponse,
  DeleteSchemaResponse,
  SchemasListResponse,
  SchemaResponse,
  Schema,
} from '../../types/schemas.types';
import ViewSchemaModal from '../../components/Schemas/ViewSchemaModal';
import CreateEditSchemaModal, {
  SchemaFormData,
} from '../../components/Schemas/CreateEditSchemaModal';
import ConfirmDeleteSchemaDialog from '../../components/Schemas/ConfirmDeleteSchemaDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './Schemas.css';

const Schemas: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { schemas, selectedSchema, loading, error, pagination, search } = useAppSelector(
    (state) => state.schemas
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState<Schema | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchSchemas, { loading: fetchingList }] = useLazyQuery<
    { schemas: SchemasListResponse },
    { params: FindSchemaListInput }
  >(GET_SCHEMAS_LIST, {
    fetchPolicy: 'network-only',
  });

  const [fetchSchema] = useLazyQuery<
    { schema: SchemaResponse },
    { params: FindSchemaInput }
  >(GET_SCHEMA, {
    fetchPolicy: 'network-only',
  });

  const [createSchemaMutation] = useMutation<
    { createSchema: CreateSchemaResponse },
    { params: CreateSchemaInput }
  >(CREATE_SCHEMA);

  const [updateSchemaMutation] = useMutation<
    { updateSchema: UpdateSchemaResponse },
    { params: UpdateSchemaInput }
  >(UPDATE_SCHEMA);

  const [deleteSchemaMutation] = useMutation<
    { deleteSchema: DeleteSchemaResponse },
    { params: DeleteSchemaInput }
  >(DELETE_SCHEMA);

  useEffect(() => {
    loadSchemas();
  }, [pagination.page, pagination.pageSize, search]);

  const loadSchemas = async () => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchSchemas({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.schemas?.success && data.schemas.data) {
        dispatch(setSchemas(data.schemas.data));
      } else {
        const errorMessage = data?.schemas?.message || 'Error al cargar esquemas';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading schemas:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchSchema({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.schema?.success && data.schema.data && data.schema.data.length > 0) {
        const schema = data.schema.data[0];
        dispatch(setSelectedSchema(schema));
        setViewModalOpen(true);
      } else {
        const errorMessage = data?.schema?.message || 'Error al cargar esquema';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading schema:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchSchema({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.schema?.success && data.schema.data && data.schema.data.length > 0) {
        const schema = data.schema.data[0];
        dispatch(setSelectedSchema(schema));
        setModalMode('edit');
        setCreateEditModalOpen(true);
      } else {
        const errorMessage = data?.schema?.message || 'Error al cargar esquema';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading schema:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedSchema());
    setModalMode('create');
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (data: SchemaFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (modalMode === 'create') {
        const { data: responseData } = await createSchemaMutation({
          variables: {
            params: {
              schemaName: data.schemaName,
              description: data.description,
            },
          },
        });

        if (responseData?.createSchema?.success) {
          if (responseData.createSchema.data) {
            dispatch(addSchema(responseData.createSchema.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedSchema());
          setSaveError(null);
        } else {
          const errorMessage =
            responseData?.createSchema?.message || 'Error al crear esquema';
          const errors = responseData?.createSchema?.errors || [];
          const fullErrorMessage =
            errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

          setSaveError(fullErrorMessage);
        }
      } else {
        if (!data.id) {
          setSaveError('ID de esquema no disponible');
          return;
        }

        const { data: responseData } = await updateSchemaMutation({
          variables: {
            params: {
              id: data.id,
              schemaName: data.schemaName,
              description: data.description,
            },
          },
        });

        if (responseData?.updateSchema?.success) {
          if (responseData.updateSchema.data) {
            dispatch(updateSchemaAction(responseData.updateSchema.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedSchema());
          setSaveError(null);
        } else {
          const errorMessage =
            responseData?.updateSchema?.message || 'Error al actualizar esquema';
          const errors = responseData?.updateSchema?.errors || [];
          const fullErrorMessage =
            errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

          setSaveError(fullErrorMessage);
        }
      }
    } catch (err: any) {
      console.error('Error saving schema:', err);
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedSchema());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedSchema());
    setSaveError(null);
    setSaving(false);
  };

  const handleDeleteClick = (schema: Schema) => {
    setSchemaToDelete(schema);
    setConfirmDeleteOpen(true);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setSchemaToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!schemaToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteSchemaMutation({
        variables: {
          params: {
            ids: [schemaToDelete.id],
          },
        },
      });

      if (responseData?.deleteSchema?.success) {
        dispatch(deleteSchemaAction(schemaToDelete.id));
        setConfirmDeleteOpen(false);
        setSchemaToDelete(null);
        setSuccessMessage('Esquema eliminado exitosamente');
      } else {
        const errorMessage =
          responseData?.deleteSchema?.message || 'Error al eliminar esquema';
        const errors = responseData?.deleteSchema?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

        setDeleteError(fullErrorMessage);
      }
    } catch (err: any) {
      console.error('Error deleting schema:', err);
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<Schema>[] = [
    {
      field: 'schemaName',
      headerName: 'Nombre del Esquema',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 2,
      minWidth: 300,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 130,
    },
    {
      field: 'updatedAt',
      headerName: 'Fecha Actualización',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="schemas-container">
        <Paper elevation={2} className="schemas-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Esquemas</Typography>
          </Breadcrumbs>

          <Box className="schemas-header">
            <Typography variant="h4" component="h1">
              Esquemas de Base de Datos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de esquemas del sistema
            </Typography>
          </Box>

          <Box className="schemas-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar esquema..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ ml: 1 }}
              >
                Buscar
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
            >
              Nuevo Esquema
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={schemas}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>

        <ViewSchemaModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditSchemaModal
          open={createEditModalOpen}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          mode={modalMode}
          saving={saving}
          saveError={saveError}
        />

        <ConfirmDeleteSchemaDialog
          open={confirmDeleteOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          schemaName={schemaToDelete?.schemaName || ''}
          deleting={deleting}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>

        {deleteError && (
          <Snackbar
            open={!!deleteError}
            autoHideDuration={6000}
            onClose={() => setDeleteError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setDeleteError(null)} severity="error" sx={{ width: '100%' }}>
              {deleteError}
            </Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default Schemas;
